'use client';

/* eslint-disable react-refresh/only-export-components */

import { useMemo } from 'react';
import { OTHERS_BUCKET_ID, type Breakdown } from '@/lib/transactions/utils';
import { type CategoryIndex, UNCATEGORIZED_META, getCategoryMeta } from './categoryIndex';

const OTHERS_COLOR = '#A0A0A0';
const OTHERS_LABEL = 'Outros';

export interface BreakdownSegmentVM {
  id: string;
  label: string;
  color: string;
  iconName: string | null;
  pct: number;
  total: number;
}

export function breakdownSegments(breakdown: Breakdown, idx: CategoryIndex): BreakdownSegmentVM[] {
  if (breakdown.total === 0) return [];
  return breakdown.segments.map((s) => {
    if (s.categoryId === OTHERS_BUCKET_ID) {
      return {
        id: OTHERS_BUCKET_ID,
        label: OTHERS_LABEL,
        color: OTHERS_COLOR,
        iconName: null,
        pct: (s.total / breakdown.total) * 100,
        total: s.total,
      };
    }
    const meta = getCategoryMeta(idx, s.categoryId);
    return {
      id: s.categoryId,
      label: meta.label,
      color: meta.color,
      iconName: meta.iconName,
      pct: (s.total / breakdown.total) * 100,
      total: s.total,
    };
  });
}

interface Props {
  breakdown: Breakdown;
  categoryIndex: CategoryIndex;
  /** Bar height in px. Default 10; M4 fatura detail uses 6. */
  height?: number;
  selectedCategoryIds?: readonly string[];
  categoryFilterIdByCategoryId?: ReadonlyMap<string, string>;
  onSelectCategory?: (categoryId: string) => void;
}

export function StackedBar({
  breakdown,
  categoryIndex,
  height = 10,
  selectedCategoryIds = [],
  categoryFilterIdByCategoryId,
  onSelectCategory,
}: Props) {
  const segments = useMemo(
    () => breakdownSegments(breakdown, categoryIndex),
    [breakdown, categoryIndex],
  );
  const ariaLabel = segments.map((s) => `${s.label} ${Math.round(s.pct)}%`).join(', ');
  void UNCATEGORIZED_META;
  const interactive = typeof onSelectCategory === 'function';

  return (
    <div
      role={interactive ? 'list' : 'img'}
      aria-label={ariaLabel}
      className="flex w-full items-stretch"
      style={{ height }}
    >
      {segments.map((s, i) => {
        const filterId = categoryFilterIdByCategoryId?.get(s.id) ?? s.id;
        const selected = selectedCategoryIds.length === 1 && selectedCategoryIds[0] === filterId;
        const filterable = interactive && s.id !== OTHERS_BUCKET_ID;
        const style = {
          width: `${s.pct}%`,
          backgroundColor: s.color,
          marginLeft: i === 0 ? 0 : 1.5,
          opacity: selectedCategoryIds.length > 0 && !selected ? 0.45 : 1,
        };

        if (!filterable) {
          return <span key={s.id} className="rounded-full" style={style} />;
        }

        return (
          <button
            key={s.id}
            type="button"
            role="listitem"
            aria-pressed={selected}
            aria-label={`Filtrar por ${s.label}`}
            onClick={() => onSelectCategory?.(filterId)}
            className="rounded-full transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            style={style}
          />
        );
      })}
    </div>
  );
}
